import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import {
  Field, FieldDescription, FieldGroup, FieldLabel, FieldLegend, FieldSet,
} from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { SearchableSelect } from '@/components/SearchableSelect'
import { DatePicker } from '@/components/DatePicker'
import { RecordDrawer } from '@/components/ui/inline-edit'

/**
 * Agreement types the wizard can create. DPA and AMENDMENT are excluded on
 * purpose: both need a parent contract this wizard does not create, and the
 * endpoint rejects them.
 */
const CONTRACT_TYPES = [
  { value: 'ICA', label: 'ICA — Independent Contractor Agreement' },
  { value: 'MSA', label: 'MSA — Master Service Agreement' },
  { value: 'SOW', label: 'SOW — Statement of Work' },
  { value: 'NDA', label: 'NDA — Non-Disclosure Agreement' },
  { value: 'PO', label: 'PO — Blanket Order (Framework)' },
] as const

/** The contractor's company — identification + trade-register data. */
const COMPANY_FIELDS = [
  { key: 'company_legal_name', label: 'Company legal name',
    placeholder: 'e.g. Müller Software UG', mono: false },
  { key: 'company_legal_form', label: 'Legal form',
    placeholder: 'e.g. SRL, PFA, GmbH, Ltd', mono: false },
  { key: 'company_registration_number', label: 'Registration #',
    placeholder: 'Trade / company register number', mono: true },
  { key: 'company_registration_authority', label: 'Registration authority',
    placeholder: 'e.g. ONRC Bucuresti, Companies House', mono: false },
  { key: 'company_tax_id', label: 'Tax ID',
    placeholder: 'Fiscal / tax identification number', mono: true },
  { key: 'company_vat_id', label: 'VAT ID',
    placeholder: 'e.g. DE123456789', mono: true },
  { key: 'company_legal_representative', label: 'Legal representative',
    placeholder: 'Authorised signatory', mono: false },
] as const

type Props = {
  open: boolean
  onClose: () => void
  onCreated?: (result: {
    employment_id: number
    person_id: number
    partner_id: number
    contract_id: number
  }) => void
}

const today = () => new Date().toISOString().slice(0, 10)

const empty = () => ({
  person_id: 0,
  start_date: today(),
  manager_id: 0,
  // Defaults to ICA, not the VendorContract model default of MSA — this wizard
  // onboards an independent contractor.
  contract_type: 'ICA' as (typeof CONTRACT_TYPES)[number]['value'],
  // The contractor's own company. A freelancer is a natural person trading
  // through a company, so this lands on the Partner as its legal entity and
  // feeds the ICA / MSA document templates. All optional.
  company_legal_name: '',
  company_legal_form: '',
  company_registration_number: '',
  company_registration_authority: '',
  company_registration_date: '',
  company_tax_id: '',
  company_vat_id: '',
  company_legal_representative: '',
})

export default function FreelancerWizard({ open, onClose, onCreated }: Props) {
  const qc = useQueryClient()
  const [form, setForm] = useState(empty)
  const set = <K extends keyof ReturnType<typeof empty>>(k: K, v: any) =>
    setForm((f) => ({ ...f, [k]: v }))

  const personsQ = useQuery({
    queryKey: ['hr', 'persons'],
    queryFn: async () =>
      (await api.get<{ id: number; full_name: string }[]>('/hr/persons/')).data,
    enabled: open,
  })

  const createMut = useMutation({
    mutationFn: async (payload: any) =>
      (await api.post('/hr/freelancer-wizard/', payload)).data,
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['finance', 'partners'] })
      qc.invalidateQueries({ queryKey: ['hr', 'persons'] })
      qc.invalidateQueries({ queryKey: ['hr', 'employments'] })
      qc.invalidateQueries({ queryKey: ['finance', 'vendor-contracts'] })
      setForm(empty())
      onCreated?.(res)
      onClose()
    },
  })

  function handleSubmit() {
    if (!form.person_id || !form.manager_id) return
    createMut.mutate({
      ...form,
      company_registration_date: form.company_registration_date || null,
    })
  }

  function handleOpenChange(v: boolean) {
    if (!v) {
      setForm(empty())
      createMut.reset()
      onClose()
    }
  }

  const persons = personsQ.data || []
  const error =
    (createMut.error as any)?.response?.data?.detail ||
    (createMut.error as any)?.message

  return (
    <RecordDrawer
      open={open}
      onOpenChange={handleOpenChange}
      title="New Freelancer"
      description="Attaches a billing shell (Partner + Vendor Contract + Employment) to an existing HR person. All other details can be completed from the freelancer's pages."
      onSubmit={handleSubmit}
      isPending={createMut.isPending}
      submitLabel="Create freelancer"
      width="wide"
    >
      <FieldGroup>
        {error && (
          <div className="p-3 bg-destructive/10 border border-destructive/20 rounded text-sm text-destructive">
            {String(error)}
          </div>
        )}

        <div className="p-3 bg-muted/60 border rounded text-sm text-muted-foreground">
          The person must already exist in HR before they can be set up as a
          freelancer.{' '}
          <a href="/admin/hr/people" className="underline underline-offset-2 hover:text-foreground">
            Go to HR → People
          </a>{' '}
          to add them first.
        </div>

        <Field>
          <FieldLabel htmlFor="fw-person">Person *</FieldLabel>
          <SearchableSelect
            id="fw-person"
            value={form.person_id ? String(form.person_id) : undefined}
            onChange={(v) => set('person_id', Number(v))}
            placeholder="— search by name —"
            options={persons.map((p) => ({ value: String(p.id), label: p.full_name }))}
          />
          <FieldDescription>
            Select the person who will work as a freelancer.
          </FieldDescription>
        </Field>

        <Field>
          <FieldLabel htmlFor="fw-mgr">People Manager *</FieldLabel>
          <SearchableSelect
            id="fw-mgr"
            value={form.manager_id ? String(form.manager_id) : undefined}
            onChange={(v) => set('manager_id', Number(v))}
            placeholder="— pick the internal manager —"
            options={persons.map((p) => ({ value: String(p.id), label: p.full_name }))}
          />
          <FieldDescription>
            Approves time, leaves, and assigns the freelancer to projects.
          </FieldDescription>
        </Field>

        <Field>
          <FieldLabel htmlFor="fw-ctype">Agreement type</FieldLabel>
          <Select
            value={form.contract_type}
            onValueChange={(v) => set('contract_type', v)}
          >
            <SelectTrigger id="fw-ctype"><SelectValue /></SelectTrigger>
            <SelectContent>
              {CONTRACT_TYPES.map((t) => (
                <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FieldDescription>
            The vendor contract created for this freelancer. An ICA is the usual
            choice for an independent contractor.
          </FieldDescription>
        </Field>

        <FieldSet>
          <FieldLegend>Contractor's company</FieldLegend>
          <FieldDescription>
            A freelancer normally invoices through their own company, so the company
            is registered as this partner's legal entity — the person stays the
            partner name, the company becomes its legal name. These values feed the
            ICA / MSA document templates. All optional, and editable later on the
            partner's page.
          </FieldDescription>
          <FieldGroup>
            <div className="grid grid-cols-2 gap-4">
              {COMPANY_FIELDS.map((f) => (
                <Field key={f.key}>
                  <FieldLabel htmlFor={`fw-${f.key}`}>{f.label}</FieldLabel>
                  <Input
                    id={`fw-${f.key}`}
                    value={form[f.key]}
                    onChange={(e) => set(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    className={f.mono ? 'font-mono' : undefined}
                  />
                </Field>
              ))}
              <Field>
                <FieldLabel htmlFor="fw-company_registration_date">Registered on</FieldLabel>
                <DatePicker
                  id="fw-company_registration_date"
                  value={form.company_registration_date}
                  onChange={(v) => set('company_registration_date', v)}
                />
              </Field>
            </div>
          </FieldGroup>
        </FieldSet>

      </FieldGroup>
    </RecordDrawer>
  )
}
